/**
 * Meal Planning — allergy-aware weekly planner.
 *
 * Plan meals across the week by adding products to day/meal slots via the
 * (OFF-backed) product search. Each added item carries the safety check for the
 * logged-in user, so meals and days roll up to a worst-case RED/YELLOW/GREEN
 * badge — surfacing unsafe items before you shop or cook.
 *
 * The plan persists per-user in localStorage (no backend needed yet).
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Plus, X, Trash2, Search } from 'lucide-react';
import Card from '../common/Card';
import Badge from '../common/Badge';
import Alert from '../common/Alert';
import useAuth from '../../hooks/useAuth';
import useAPI from '../../hooks/useAPI';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MEALS = ['Breakfast', 'Lunch', 'Dinner', 'Snacks'];

const slotKey = (day, meal) => `${day}__${meal}`;
const storageKey = (userId) => `aisafeplate.mealplan.${userId || 'anon'}`;

// Worst-case safety across a set of items. RED beats YELLOW beats GREEN.
const RANK = { RED: 3, YELLOW: 2, GREEN: 1 };
const worstLevel = (items) =>
  items.reduce((worst, it) => (RANK[it.safety_level] > RANK[worst] ? it.safety_level : worst), 'GREEN');

function SafetyBadge({ level, size }) {
  if (level === 'RED') return <Badge variant="danger" size={size}>🚫 Unsafe</Badge>;
  if (level === 'YELLOW') return <Badge variant="warning" size={size}>⚠️ Caution</Badge>;
  return <Badge variant="success" size={size}>✅ Safe</Badge>;
}

export default function MealPlanning() {
  const { user } = useAuth();
  const { get } = useAPI();

  // plan: { [slotKey]: [{ id, name, brand, safety_level, matched_allergens }] }
  const [plan, setPlan] = useState({});
  const [activeSlot, setActiveSlot] = useState(null); // { day, meal }
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const key = storageKey(user?.id);

  // Load saved plan when the user changes.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(key);
      setPlan(raw ? JSON.parse(raw) : {});
    } catch {
      setPlan({});
    }
  }, [key]);

  // Persist on every change.
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(plan));
    } catch {
      /* storage full / unavailable — plan stays in memory for this session */
    }
  }, [key, plan]);

  const openSlot = (day, meal) => {
    setActiveSlot({ day, meal });
    setQuery('');
    setResults([]);
    setError('');
  };

  const closeSlot = () => {
    setActiveSlot(null);
    setQuery('');
    setResults([]);
    setError('');
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const q = query.trim();
    if (q.length < 2) {
      setError('Type at least 2 characters to search.');
      return;
    }
    setLoading(true);
    setError('');
    setResults([]);
    try {
      const data = await get(`/api/products/search?q=${encodeURIComponent(q)}&limit=8`);
      if (!data.results || data.results.length === 0) {
        setError('No products found. Try a different search.');
      } else {
        setResults(data.results);
      }
    } catch (err) {
      setError(err.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const addItem = ({ product, safety_check }) => {
    if (!activeSlot) return;
    const k = slotKey(activeSlot.day, activeSlot.meal);
    const item = {
      id: `${product.barcode || product.name}-${Date.now()}`,
      name: product.name,
      brand: product.brand || null,
      safety_level: safety_check?.safety_level || 'GREEN',
      matched_allergens: safety_check?.matched_allergens || [],
    };
    setPlan((prev) => ({ ...prev, [k]: [...(prev[k] || []), item] }));
    closeSlot();
  };

  const removeItem = (k, id) => {
    setPlan((prev) => {
      const next = (prev[k] || []).filter((it) => it.id !== id);
      const copy = { ...prev };
      if (next.length) copy[k] = next;
      else delete copy[k];
      return copy;
    });
  };

  const clearWeek = () => {
    if (window.confirm('Clear the entire week? This cannot be undone.')) setPlan({});
  };

  // Week-level stats.
  const stats = useMemo(() => {
    const all = Object.values(plan).flat();
    const flagged = all.filter((it) => it.safety_level === 'RED' || it.safety_level === 'YELLOW');
    return {
      total: all.length,
      flagged: flagged.length,
      unsafe: all.filter((it) => it.safety_level === 'RED').length,
    };
  }, [plan]);

  return (
    <div className="page-container">
      <h1 className="page-title">Meal Planning</h1>
      <p className="dining-intro">
        Plan your week and catch unsafe items before you shop. Add products to any
        meal — each is checked against your allergies, and days roll up to a
        worst-case rating.
      </p>

      {/* Week summary */}
      <Card className="meal-summary-card">
        {stats.total === 0 ? (
          <p className="meal-summary-empty">Nothing planned yet — tap “Add” on a meal to start.</p>
        ) : (
          <div className="meal-summary-row">
            <div>
              <span className="meal-summary-num">{stats.total}</span> item
              {stats.total === 1 ? '' : 's'} planned
            </div>
            {stats.flagged > 0 ? (
              <Badge variant={stats.unsafe > 0 ? 'danger' : 'warning'}>
                {stats.unsafe > 0 ? `${stats.unsafe} unsafe` : `${stats.flagged} to double-check`}
              </Badge>
            ) : (
              <Badge variant="success">All clear</Badge>
            )}
          </div>
        )}
      </Card>

      {/* Add panel (shown when a slot is active) */}
      {activeSlot && (
        <Card className="meal-add-panel">
          <div className="meal-add-head">
            <span>
              Add to <strong>{activeSlot.day}</strong> · {activeSlot.meal}
            </span>
            <button className="icon-btn" onClick={closeSlot} aria-label="Cancel">
              <X size={18} />
            </button>
          </div>
          <form onSubmit={handleSearch} className="meal-add-search">
            <input
              type="text"
              autoFocus
              placeholder="Search a product, e.g. yogurt"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="input input-lg"
              disabled={loading}
            />
            <button type="submit" disabled={loading} className="btn btn-primary">
              <Search size={18} />
            </button>
          </form>
          {error && <Alert variant="error" title="" message={error} />}
          {loading && <p className="text-center">Searching…</p>}
          {results.length > 0 && (
            <ul className="meal-result-list">
              {results.map((r) => (
                <li key={r.product.barcode || r.product.name}>
                  <button className="meal-result" onClick={() => addItem(r)}>
                    <span
                      className={`meal-dot meal-dot-${(r.safety_check?.safety_level || 'GREEN').toLowerCase()}`}
                    />
                    <span className="meal-result-text">
                      <span className="meal-result-name">{r.product.name}</span>
                      {r.product.brand && <span className="meal-result-brand">{r.product.brand}</span>}
                    </span>
                    <Plus size={16} className="meal-result-add" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {/* Week grid */}
      {DAYS.map((day) => {
        const dayItems = MEALS.flatMap((m) => plan[slotKey(day, m)] || []);
        return (
          <Card key={day} className="mealplan-day">
            <div className="mealplan-day-head">
              <h2 className="mealplan-day-name">{day}</h2>
              {dayItems.length > 0 && <SafetyBadge level={worstLevel(dayItems)} />}
            </div>

            {MEALS.map((meal) => {
              const k = slotKey(day, meal);
              const items = plan[k] || [];
              return (
                <div key={meal} className="mealplan-meal">
                  <div className="mealplan-meal-label">{meal}</div>
                  <div className="mealplan-meal-body">
                    {items.map((it) => (
                      <div key={it.id} className="mealplan-item">
                        <span className={`meal-dot meal-dot-${it.safety_level.toLowerCase()}`} />
                        <span className="mealplan-item-name">
                          {it.name}
                          {it.matched_allergens?.length > 0 && (
                            <span className="mealplan-item-flag"> — {it.matched_allergens.join(', ')}</span>
                          )}
                        </span>
                        <button
                          className="icon-btn"
                          onClick={() => removeItem(k, it.id)}
                          aria-label={`Remove ${it.name}`}
                        >
                          <X size={15} />
                        </button>
                      </div>
                    ))}
                    <button className="mealplan-add-btn" onClick={() => openSlot(day, meal)}>
                      <Plus size={14} /> Add
                    </button>
                  </div>
                </div>
              );
            })}
          </Card>
        );
      })}

      {stats.total > 0 && (
        <button className="btn btn-secondary btn-full mealplan-clear" onClick={clearWeek}>
          <Trash2 size={16} /> Clear week
        </button>
      )}
    </div>
  );
}
